import React, { useMemo, useState } from 'react';
import { Button, Space, Input, Form, Select, Row, Col, Tag, Typography, notification } from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { TableCustom } from '../../../components/TableCustom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { AppSelect } from '../../../constants/appSelect';
import { newObjectKeys } from '../../../utils/utility';
import { deleteMiniAppRequest, getListMiniAppsRequest } from '../../../services/onlinestore/miniAppsServiceRequest';
import { getListPartnersRequest } from '../../../services/onlinestore/partnerServiceRequest';

const { Text } = Typography;

const QuanLyMiniApps = () => {
  const navigate = useNavigate();
  const [formSearch] = Form.useForm();
  const [params, setParams] = useState({
    page: 1,
    limit: 20,
  });

  const {
    data: dataMiniApps,
    isPending,
    refetch: refetchDataMiniApps,
  } = useQuery({ queryKey: ['dataMiniApps', params], queryFn: () => getListMiniAppsRequest(params) });

  // get data partner
  const { data: dataPartners } = useQuery({
    queryKey: ['dataPartners'],
    queryFn: () => getListPartnersRequest({ page: 1, limit: 20 }),
  });

  // convert list partners options
  const dataPartnersOption = useMemo(() => {
    const options =
      dataPartners?.data?.map((item) => {
        return { label: item?.title, value: item?.id };
      }) || [];
    return [{ label: 'Tất cả', value: -1 }, ...options];
  }, [dataPartners]);

  const { mutate: mutateDeleteMiniApp } = useMutation({
    mutationFn: deleteMiniAppRequest,
    onSuccess: async (data) => {
      if (data?.status === 1) {
        notification.success({
          message: 'Xóa miniapp thành công!',
        });
        refetchDataMiniApps();
      } else {
        notification.error({
          message: data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!',
        });
      }
    },
    onError: (error) => {
      notification.error({
        message: error.data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!',
      });
    },
  });

  const handleSubmitForm = (values) => {
    const temp = { page: 1, limit: params.limit };
    if (values?.title) {
      Object.assign(temp, { title: values?.title });
    }

    if (values?.partner_id >= 0) {
      Object.assign(temp, { partner_id: [values?.partner_id] });
    }

    if (values?.status >= 0) {
      Object.assign(temp, { status: [values?.status] });
    }

    setParams(temp);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Tên miniapp',
      dataIndex: ['id', 'title'],
      key: 'id',
      render: (_, { title, id }) => (
        <Link to={`/online-store/quan-ly-mini-apps/${id}/sua-mini-app`} className="text-[#1677FF]">
          {title}
        </Link>
      ),
    },
    {
      title: 'Đối tác',
      dataIndex: 'partner_name',
      key: 'partner_id',
      render: (partner_name) => <Text className="text-[#667085]">{partner_name}</Text>,
    },
    {
      title: 'Icon',
      dataIndex: 'icon',
      key: 'icon',
      render: (icon) => (icon ? <img src={icon} alt="" className="w-10 h-auto object-contain" /> : ''),
    },
    {
      title: 'Link khởi chạy',
      dataIndex: 'open_url',
      key: 'open_url',
    },
    {
      title: 'Link kết thúc',
      dataIndex: 'close_url',
      key: 'close_url',
    },

    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag className="px-2 py-1" color={AppSelect.STATUS?.find((item) => item?.value === status)?.color}>
          {AppSelect.STATUS?.find((item) => item?.value === status)?.label}
        </Tag>
      ),
    },
    {
      title: 'Cập nhật lần cuối',
      dataIndex: ['updated_by', 'updated_at'],
      key: 'updated_at',
      render: (_, { updated_by, updated_at }) => (
        <Space direction="vertical">
          {updated_by && <Text className="text-[#667085]">{updated_by}</Text>}
          {updated_at && <Text className="text-[#667085]">{updated_at}</Text>}
        </Space>
      ),
    },
    {
      title: 'Thao tác',
      key: 'action',
      render: (_, record) => (
        <Space size={0}>
          <Button type="text" onClick={() => navigate(`/online-store/quan-ly-mini-apps/${record?.id}/sua-mini-app`)}>
            <EditOutlined />
          </Button>
          <Button type="text" onClick={() => mutateDeleteMiniApp({ id: record?.id })}>
            <DeleteOutlined />
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="m-5">
      <div className="flex justify-between items-center mb-5">
        <h1 className="text-2xl">Danh sách miniapps</h1>
        <Link to={'/online-store/quan-ly-mini-apps/them-mini-app'}>
          <Button type="primary">
            <PlusOutlined />
            Thêm miniapp
          </Button>
        </Link>
      </div>

      <Form
        name="search_blocks"
        form={formSearch}
        layout="vertical"
        requiredMark={false}
        className="mb-5"
        onFinish={handleSubmitForm}
      >
        <Row gutter={[16, 16]}>
          {/* First column: Partner name input */}
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="title" label={<Text>Tên miniapp</Text>}>
              <Input placeholder="Nhập tên miniapp" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="partner_id" label={<Text>Đối tác</Text>} initialValue={-1}>
              <Select
                showSearch
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={dataPartnersOption}
              />
            </Form.Item>
          </Col>
          {/* Second column: Status dropdown */}
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="status" label={<Text>Trạng thái</Text>} initialValue={-1}>
              <Select
                showSearch
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={[
                  { value: -1, label: 'Tất cả' },
                  { value: 0, label: 'Chưa duyệt' },
                  { value: 1, label: 'Đã duyệt' },
                ]}
              />
            </Form.Item>
          </Col>
          <Col className="flex items-center gap-2">
            <Button type="primary" htmlType="submit">
              <SearchOutlined />
              Tìm kiếm
            </Button>
            {params &&
              Object.values(newObjectKeys(params, ['title', 'partner_id', 'status'])).some((value) => value) && (
                <Button
                  htmlType="button"
                  onClick={() => {
                    formSearch.resetFields();
                    setParams({ page: 1, limit: params.limit });
                  }}
                >
                  <DeleteOutlined />
                  Xóa lọc
                </Button>
              )}
          </Col>
        </Row>
      </Form>

      <TableCustom
        columns={columns}
        dataSource={dataMiniApps?.data}
        totalItem={dataMiniApps?.total}
        params={params}
        setParams={setParams}
      />
    </div>
  );
};

export default QuanLyMiniApps;
